import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBaseUrl } from "../config/api";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { getAuthorDisplayName } from "../utils/displayName";

/**
 * Intern / seeker inbox: people who viewed their profile.
 */
export default function ProfileViewsInbox({ user }) {
  const API_URL = getApiBaseUrl();
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        user_id: String(user.id),
        mark_read: "1",
        limit: "50",
      });
      const res = await fetch(`${API_URL}/get_profile_views.php?${params}`);
      const data = await res.json();
      if (!data.success) {
        setError(data.message || "Failed to load profile views.");
        setViews([]);
        return;
      }
      setViews(data.views || []);
    } catch (err) {
      setError(err?.message || "Network error.");
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user) {
    return (
      <div style={{ maxWidth: 640, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <p>Please log in to view your inbox.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 680, margin: "24px auto", padding: "0 16px 40px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22, color: "#102a43" }}>Inbox</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
            People who viewed your profile
          </p>
        </div>
        <Link
          to="/home"
          style={{ fontSize: 13, fontWeight: 600, color: "#1d4ed8", textDecoration: "none" }}
        >
          Back to home
        </Link>
      </div>

      {loading && <p style={{ color: "#64748b" }}>Loading…</p>}
      {error && !loading && <p style={{ color: "#b91c1c" }}>{error}</p>}

      {!loading && !error && views.length === 0 && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e4ebf0",
            borderRadius: 12,
            padding: 28,
            textAlign: "center",
            color: "#64748b",
          }}
        >
          No profile views yet.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {views.map((item) => {
          const viewer = item.viewer || {};
          const name = getAuthorDisplayName(viewer);
          const when = item.updated_at
            ? new Date(item.updated_at).toLocaleString()
            : "";
          const fromOpenToWork = item.source === "looking_for_job";
          return (
            <Link
              key={item.id}
              to={`/profile/${viewer.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 14px",
                background: "#fff",
                border: "1px solid #e4ebf0",
                borderRadius: 12,
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <img
                src={resolveAvatarUrl(viewer, API_URL)}
                alt=""
                onError={handleAvatarError}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  objectFit: "cover",
                  background: "#e2e8f0",
                }}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#102a43", fontSize: 15 }}>
                  {name}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {fromOpenToWork
                    ? "Viewed your open-to-work post"
                    : "Viewed your profile"}
                  {when ? ` · ${when}` : ""}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
